import React, { ReactNode, ComponentType, ReactElement } from 'react';
import { Box, Typography, Alert } from '@mui/material';
import { ContextLoading } from './ContextLoading';
import { SkeletonLoader } from './SkeletonLoader';

export type LoadingState = 'loading' | 'error' | 'success' | 'idle';

export interface LoadingBoundaryProps {
  /** Children to render when loading is complete */
  children: ReactNode;
  /** Current loading state */
  state: LoadingState;
  /** Loading message to display */
  loadingMessage?: string;
  /** Error message when state is 'error' */
  errorMessage?: string;
  /** Custom loading component */
  loadingComponent?: ReactElement;
  /** Custom error component */
  errorComponent?: ReactElement;
  /** Skeleton variant for loading state */
  skeletonVariant?: 'text' | 'card' | 'table' | 'dashboard' | 'list';
  /** Number of skeleton items to show */
  skeletonCount?: number;
  /** Minimum loading time in ms to prevent flash */
  minLoadTime?: number;
  /** Show retry button on error */
  showRetry?: boolean;
  /** Retry handler */
  onRetry?: () => void;
  /** Full height container for page-level loading */
  fullHeight?: boolean;
  /** Additional class name */
  className?: string;
}

interface LoadingBoundaryState {
  showLoading: boolean;
  hasMinTimePassed: boolean;
}

/**
 * LoadingBoundary - Higher-order component for managing loading states
 * 
 * Features:
 * - Prevents loading flashes with minimum display time
 * - Customizable loading and error states
 * - Built-in skeleton loading patterns
 * - Context-aware loading management
 * - Graceful error handling with retry mechanism
 */
export class LoadingBoundary extends React.Component<LoadingBoundaryProps, LoadingBoundaryState> {
  private minTimeTimer: NodeJS.Timeout | null = null;

  constructor(props: LoadingBoundaryProps) {
    super(props);
    this.state = {
      showLoading: props.state === 'loading',
      hasMinTimePassed: false,
    };
  }

  componentDidMount() {
    this.handleStateChange();
  }

  componentDidUpdate(prevProps: LoadingBoundaryProps) {
    if (prevProps.state !== this.props.state) {
      this.handleStateChange();
    }
  }

  componentWillUnmount() {
    if (this.minTimeTimer) {
      clearTimeout(this.minTimeTimer);
    }
  }

  private handleStateChange = () => {
    const { state, minLoadTime = 300 } = this.props;

    if (state === 'loading') {
      this.setState({ showLoading: true, hasMinTimePassed: false });
      
      // Set minimum loading time
      this.minTimeTimer = setTimeout(() => {
        this.setState({ hasMinTimePassed: true });
      }, minLoadTime);
    } else {
      // If minimum time hasn't passed, wait for it
      if (!this.state.hasMinTimePassed && this.minTimeTimer) {
        // Keep showing loading until minimum time passes
        return;
      }
      
      this.setState({ showLoading: false });
      if (this.minTimeTimer) {
        clearTimeout(this.minTimeTimer);
        this.minTimeTimer = null;
      }
    }
  };

  private renderLoading = (): ReactElement => {
    const { 
      loadingComponent, 
      loadingMessage = 'Loading...', 
      skeletonVariant = 'card',
      skeletonCount = 3,
      fullHeight 
    } = this.props;

    if (loadingComponent) {
      return loadingComponent;
    }

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 3,
          height: fullHeight ? '100vh' : 'auto',
          minHeight: fullHeight ? 'unset' : 200,
        }}
      >
        <ContextLoading message={loadingMessage} />
        <Box sx={{ mt: 2, width: '100%', maxWidth: 600 }}>
          <SkeletonLoader 
            variant={skeletonVariant} 
            count={skeletonCount}
            animate
          />
        </Box>
      </Box>
    );
  };

  private renderError = (): ReactElement => {
    const { 
      errorComponent, 
      errorMessage = 'An error occurred while loading.', 
      showRetry = true,
      onRetry,
      fullHeight 
    } = this.props;

    if (errorComponent) {
      return errorComponent;
    }

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 3,
          height: fullHeight ? '100vh' : 'auto',
          minHeight: fullHeight ? 'unset' : 200,
        }}
      >
        <Alert 
          severity="error" 
          action={
            showRetry && onRetry ? (
              <Typography
                component="button"
                onClick={onRetry}
                sx={{
                  background: 'none',
                  border: 'none',
                  color: 'error.main',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  fontWeight: 'medium',
                }}
              >
                Retry
              </Typography>
            ) : undefined
          }
          sx={{ maxWidth: 500 }}
        >
          {errorMessage}
        </Alert>
      </Box>
    );
  };

  render() {
    const { children, state, className, fullHeight } = this.props;
    const { showLoading } = this.state;

    const shouldShowLoading = showLoading || state === 'loading';

    if (shouldShowLoading) {
      return this.renderLoading();
    }

    if (state === 'error') {
      return this.renderError();
    }

    // Success or idle state - render children
    return (
      <Box 
        className={className}
        sx={{ 
          height: fullHeight ? '100%' : 'auto',
          minHeight: fullHeight ? '100vh' : 'auto',
        }}
      >
        {children}
      </Box>
    );
  }
}

/**
 * Higher-order component that wraps a component with LoadingBoundary
 */
export interface WithLoadingBoundaryOptions {
  /** Default loading message */
  loadingMessage?: string;
  /** Default error message */
  errorMessage?: string;
  /** Skeleton variant for loading */
  skeletonVariant?: LoadingBoundaryProps['skeletonVariant'];
  /** Number of skeleton items */
  skeletonCount?: number;
  /** Minimum loading time */
  minLoadTime?: number;
  /** Full height container */
  fullHeight?: boolean;
}

export function withLoadingBoundary<P extends object>(
  Component: ComponentType<P>,
  options: WithLoadingBoundaryOptions = {}
) {
  const WrappedComponent = React.forwardRef<any, P & { 
    loadingState?: LoadingState;
    loadingMessage?: string;
    errorMessage?: string;
    onRetry?: () => void;
  }>((props, ref) => {
    const {
      loadingState = 'idle',
      loadingMessage = options.loadingMessage,
      errorMessage = options.errorMessage,
      onRetry,
      ...componentProps
    } = props;

    return (
      <LoadingBoundary
        state={loadingState}
        loadingMessage={loadingMessage}
        errorMessage={errorMessage}
        skeletonVariant={options.skeletonVariant}
        skeletonCount={options.skeletonCount}
        minLoadTime={options.minLoadTime}
        fullHeight={options.fullHeight}
        onRetry={onRetry}
        showRetry={!!onRetry}
      >
        <Component {...(componentProps as P)} ref={ref} />
      </LoadingBoundary>
    );
  });

  WrappedComponent.displayName = `withLoadingBoundary(${Component.displayName || Component.name})`;
  
  return WrappedComponent;
}

export default LoadingBoundary;